import { Badge, Box, Card, Collapsible, Heading, HStack, Text, VStack } from '@chakra-ui/react'
import { ChevronDown, ChevronRight, ExternalLink, Quote } from 'lucide-react'
import { useState } from 'react'
import type { CustomerStory } from '@/data/business-value'

interface CustomerStoryCardProps {
  story: CustomerStory
  color?: string
}

export default function CustomerStoryCard({ story, color = 'blue.500' }: CustomerStoryCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Card.Root overflow="hidden" transition="all 0.3s" _hover={{ shadow: 'lg' }} h="100%">
      <Card.Body p={0}>
        <Box
          p={5}
          cursor="pointer"
          onClick={() => setIsOpen(!isOpen)}
          _hover={{ bg: 'bg.subtle' }}
          transition="background 0.2s"
        >
          <VStack gap={3} align="start">
            <HStack gap={2} justify="space-between" w="100%">
              <Heading size="sm">{story.name}</Heading>
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </HStack>
            <Badge size="sm" colorPalette="gray" variant="subtle">
              {story.industry}
            </Badge>
            <Text fontSize="sm" color="content.secondary">
              {story.headline}
            </Text>
          </VStack>
        </Box>

        <Collapsible.Root open={isOpen}>
          <Collapsible.Content>
            <Box px={5} pb={5} borderTop="1px solid" borderColor="border">
              <VStack gap={3} align="start" pt={4}>
                <VStack gap={1} align="start" w="100%">
                  {story.metrics.map((metric) => (
                    <HStack key={metric} gap={2}>
                      <Box w={1.5} h={1.5} borderRadius="full" bg={color} flexShrink={0} mt={1} />
                      <Text fontSize="sm">{metric}</Text>
                    </HStack>
                  ))}
                </VStack>

                {story.quote && (
                  // Accent stripe keeps the ICP's identity color (a token, e.g. "blue.500")
                  // but uses the logical `borderInlineStart` property instead of `borderLeft`
                  // so it flips correctly in RTL layouts - a DayN-consistent token treatment.
                  <Box
                    p={3}
                    borderInlineStart="3px solid"
                    borderColor={color}
                    bg="bg.subtle"
                    borderRadius="md"
                    w="100%"
                  >
                    <HStack gap={2} align="start">
                      <Quote size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <Text fontSize="sm" fontStyle="italic" color="content.secondary">
                        {story.quote}
                      </Text>
                    </HStack>
                  </Box>
                )}

                {story.caseStudyUrl && (
                  <a
                    href={story.caseStudyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <HStack gap={1} color={color} fontSize="sm" cursor="pointer">
                      <Text>Read case study</Text>
                      <ExternalLink size={14} />
                    </HStack>
                  </a>
                )}
              </VStack>
            </Box>
          </Collapsible.Content>
        </Collapsible.Root>
      </Card.Body>
    </Card.Root>
  )
}
